import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Avatar,
  Divider,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Visibility as ViewIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import SysDateRangePicker from '../components/SysDateRangePicker';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Log, LogFilters, LogStats, ApiResponse } from '../types';
import logService from '../services/logService';
import LogSettings from '../components/LogSettings';
import CronExecutionsModal from '../components/modals/CronExecutionsModal';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

// Função auxiliar para formatar data sem problemas de fuso horário (usado apenas para filtros)
const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Função auxiliar para converter string YYYY-MM-DD para Date sem problemas de fuso horário
const stringToDateLocal = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  try {
    // Parse manual para evitar problemas de fuso horário
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month é 0-indexed
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) return null;
    
    return date;
  } catch (error) {
    console.error('Erro ao converter string para Date:', error);
    return null;
  }
};

const LogsPage: React.FC = () => {
  const theme = useTheme();
  // Obter data de hoje para filtro inicial (corrigido para fuso horário local)
  const today = new Date();
  const todayString = formatDateToString(today);

  const [filters, setFilters] = useState<LogFilters & { page?: number; limit?: number }>({
    page: 1,
    limit: 25,
    startDate: todayString, // Filtrar apenas logs de hoje
    endDate: todayString    // Filtrar apenas logs de hoje
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);  
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [showLogSettings, setShowLogSettings] = useState(false);
  const [showCronExecutions, setShowCronExecutions] = useState(false);

  // Query para buscar logs
  const {
    data: logsResponse,
    isLoading: isLoadingLogs,
    error: logsError,
    refetch: refetchLogs
  } = useQuery<ApiResponse<Log[]>>({
    queryKey: ['logs', filters],
    queryFn: () => logService.getLogs(filters),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0, // Sempre considerar dados como stale para garantir atualização
    gcTime: 1000 * 60 * 2, // Cache por 2 minutos apenas
  });

  const logs = logsResponse?.data || [];
  const pagination = logsResponse?.pagination;

  // Effect para garantir que os dados sejam carregados quando o componente monta
  useEffect(() => {
    // Force refetch quando o componente é montado
    refetchLogs();
  }, [refetchLogs]); // Inclui refetchLogs como dependência

  // Handlers
  const handleFilterChange = (field: keyof (LogFilters & { page?: number; limit?: number }), value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset para primeira página ao filtrar
    }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 1
    }));
  };

  const handleExpandRow = (logId: number) => {
    setExpandedRows(prev =>
      prev.includes(logId)
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const handleViewLogDetails = (log: Log) => {
    setSelectedLog(log);
    setShowLogDetails(true);
  };

  const handleRefreshLogs = () => {
    refetchLogs();
    toast.success('Logs atualizados!');
  };

  const clearFilters = () => {
    const today = new Date();
    const todayString = formatDateToString(today);
    
    setFilters({
      page: 1,
      limit: 25,
      startDate: todayString,
      endDate: todayString
    });
  };

  const showAllLogs = () => {
    setFilters({
      page: 1,
      limit: 25
    });
  };

  // Função para formatar dados JSON
  const formatJsonData = (data: any) => {
    if (!data) return 'Nenhum dado';
    return JSON.stringify(data, null, 2);
  };

  if (logsError) {
    return (
      <Box sx={pageShellSx}>
        <Alert severity="error">Erro ao carregar logs: {(logsError as any)?.message}</Alert>
      </Box>
    );
  }

  const totalLogs = pagination?.total ?? logs.length;
  const hasActiveFilters = !!(filters.tipo || filters.acao || (filters.startDate && filters.endDate && (filters.startDate !== todayString || filters.endDate !== todayString)));

  return (
      <Box sx={pageShellSx}>
        <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'secondary')}>
          <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
            <HistoryIcon sx={{ fontSize: 120, color: theme.palette.secondary.main }} />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}` }}>
                <HistoryIcon sx={{ fontSize: 22 }} />
              </Avatar>
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                  <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Logs do Sistema</Typography>
                  {filters.startDate && filters.endDate && filters.startDate === filters.endDate && filters.startDate === todayString ? (
                    <Chip label="Logs de Hoje" color="primary" size="small" icon={<DateIcon />} sx={{ height: 20, fontSize: '0.65rem' }} />
                  ) : hasActiveFilters ? (
                    <Chip label="Filtros Aplicados" color="warning" size="small" icon={<FilterIcon />} sx={{ height: 20, fontSize: '0.65rem' }} />
                  ) : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">Auditoria de ações e eventos do sistema</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={handleRefreshLogs} disabled={isLoadingLogs}>Atualizar</Button>
              <Button variant="outlined" size="small" startIcon={<ScheduleIcon />} onClick={() => setShowCronExecutions(true)}>Cron</Button>
              <Button variant="outlined" size="small" startIcon={<SettingsIcon />} onClick={() => setShowLogSettings(true)}>Config.</Button>
              <Button variant={showFilters ? 'contained' : 'outlined'} size="small" startIcon={<FilterIcon />} onClick={() => setShowFilters(!showFilters)}>Filtros</Button>
            </Stack>
          </Stack>
        </Paper>

        {!isLoadingLogs && totalLogs > 0 && (
          <Grid container spacing={1} mb={1.5}>
            <Grid item xs={6} md={4}><SysKpiCard titulo="Total de Logs" valor={totalLogs} icone={<HistoryIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
            <Grid item xs={6} md={4}><SysKpiCard titulo="Nesta Página" valor={logs.length} icone={<ViewIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
            <Grid item xs={6} md={4}><SysKpiCard titulo="Página" valor={pagination?.page ?? 1} subtitulo={pagination ? `de ${Math.max(1, Math.ceil(pagination.total / pagination.limit))}` : undefined} icone={<DateIcon sx={{ color: theme.palette.secondary.main }} />} cor={theme.palette.secondary.main} bgCor={alpha(theme.palette.secondary.main, 0.12)} /></Grid>
          </Grid>
        )}

        <Collapse in={showFilters}>
          <Paper elevation={0} sx={{ ...filtersPaperSx(theme), mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
              <FilterIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography variant="body2" fontWeight={700}>Filtros</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={filters.tipo || ''}
                    label="Tipo"
                    onChange={(e) => handleFilterChange('tipo', e.target.value || undefined)}
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="cliente">Cliente</MenuItem>
                    <MenuItem value="contrato">Contrato</MenuItem>
                    <MenuItem value="fatura">Fatura</MenuItem>
                    <MenuItem value="sistema">Sistema</MenuItem>
                    <MenuItem value="autenticacao">Autenticação</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Ação</InputLabel>
                  <Select
                    value={filters.acao || ''}
                    label="Ação"
                    onChange={(e) => handleFilterChange('acao', e.target.value || undefined)}
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="criacao">Criação</MenuItem>
                    <MenuItem value="edicao">Edição</MenuItem>
                    <MenuItem value="exclusao">Exclusão</MenuItem>
                    <MenuItem value="pagamento">Pagamento</MenuItem>
                    <MenuItem value="cancelamento">Cancelamento</MenuItem>
                    <MenuItem value="autenticacao">Autenticação</MenuItem>
                    <MenuItem value="logout">Logout</MenuItem>
                    <MenuItem value="bloqueio">Bloqueio</MenuItem>
                    <MenuItem value="ativacao">Ativação</MenuItem>
                    <MenuItem value="desativacao">Desativação</MenuItem>
                    <MenuItem value="outros">Outros</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <SysDateRangePicker
                  value={{
                    startDate: filters.startDate ? stringToDateLocal(filters.startDate) : null,
                    endDate: filters.endDate ? stringToDateLocal(filters.endDate) : null
                  }}
                  onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                    setFilters(prev => ({
                      ...prev,
                      startDate: range.startDate ? formatDateToString(range.startDate) : undefined,
                      endDate: range.endDate ? formatDateToString(range.endDate) : undefined,
                      page: 1
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" gap={1}>
                  <Button variant="outlined" size="small" onClick={clearFilters}>
                    Logs de Hoje
                  </Button>
                  <Button variant="outlined" size="small" color="secondary" onClick={showAllLogs}>
                    Todos os Logs
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        <Paper elevation={0} sx={listSectionPaperSx(theme)}>
          <Box sx={listSectionHeaderBarSx(theme)}>
            <Typography variant="body2" fontWeight={700}>Listagem de Logs</Typography>
            <Chip size="small" label={`${totalLogs} registro${totalLogs !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          </Box>

          {isLoadingLogs ? (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={32} />
              <Typography variant="caption" color="text.secondary">Carregando logs...</Typography>
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ p: 2.5, textAlign: 'center' }}>
              <HistoryIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={600}>Nenhum log encontrado</Typography>
            </Box>
          ) : (
            <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={rankingTableHeadRowSx(theme)}>
                  <TableCell>ID</TableCell>
                  <TableCell>Ação</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Usuário</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {logs.map((log: Log) => (
                    <React.Fragment key={log.id}>
                      <TableRow hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                        <TableCell>
                          {log.id}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={logService.formatLogType(log.tipo)}
                            color={logService.getTypeColor(log.tipo) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={logService.formatLogAction(log.acao)}
                            color={logService.getActionColor(log.acao) as any}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {log.descricao}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {log.usuario?.usuario || 'Sistema'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Ver detalhes">
                              <IconButton
                                size="small"
                                onClick={() => handleViewLogDetails(log)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={expandedRows.includes(log.id) ? "Recolher" : "Expandir"}>
                              <IconButton
                                size="small"
                                onClick={() => handleExpandRow(log.id)}
                              >
                                {expandedRows.includes(log.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {/* Linha expandida */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={expandedRows.includes(log.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1, px: 2 }}>
                              <Grid container spacing={1.5}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    Informações Adicionais
                                  </Typography>
                                  <Box sx={{ pl: 1 }}>
                                    {log.entidade_tipo && (
                                      <Typography variant="body2">
                                        <strong>Entidade:</strong> {log.entidade_tipo} #{log.entidade_id}
                                      </Typography>
                                    )}
                                    {log.ip_address && (
                                      <Typography variant="body2">
                                        <strong>IP:</strong> {log.ip_address}
                                      </Typography>
                                    )}
                                    {log.user_agent && (
                                      <Typography variant="body2" noWrap>
                                        <strong>User Agent:</strong> {log.user_agent}
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  {log.metadata && (
                                    <>
                                      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                        Metadados
                                      </Typography>
                                      <Box sx={{ pl: 1 }}>
                                        <pre style={{ fontSize: '0.7rem', overflow: 'auto', margin: 0 }}>
                                          {formatJsonData(log.metadata)}
                                        </pre>
                                      </Box>
                                    </>
                                  )}
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && (
            <>
            <Divider />
            <Box sx={tablePaginationWrapperSx(theme)}>
              <TablePagination
                component="div"
                size="small"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelRowsPerPage="Linhas:"
                sx={{ '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 } }}
              />
            </Box>
            </>
          )}
            </>
          )}
        </Paper>

        {/* Dialog de Detalhes do Log */}
        <Dialog
          open={showLogDetails}
          onClose={() => setShowLogDetails(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Detalhes do Log #{selectedLog?.id}
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {selectedLog && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Informações Básicas</Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography><strong>Tipo:</strong> {logService.formatLogType(selectedLog.tipo)}</Typography>
                    <Typography><strong>Ação:</strong> {logService.formatLogAction(selectedLog.acao)}</Typography>
                    <Typography><strong>Descrição:</strong> {selectedLog.descricao}</Typography>
                    <Typography><strong>Usuário:</strong> {selectedLog.usuario?.nome || 'Sistema'}</Typography>
                    <Typography><strong>Data:</strong> {format(new Date(selectedLog.createdAt), 'dd/MM/yyyy HH:mm:ss')}</Typography>
                    {selectedLog.entidade_tipo && (
                      <Typography><strong>Entidade:</strong> {selectedLog.entidade_tipo} #{selectedLog.entidade_id}</Typography>
                    )}
                  </Box>
                </Grid>

                {selectedLog.dados_antes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Dados Antes da Alteração</Typography>
                    <Box sx={{ pl: 1 }}>
                      <pre style={{ fontSize: '0.875rem', overflow: 'auto', backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px' }}>
                        {formatJsonData(selectedLog.dados_antes)}
                      </pre>
                    </Box>
                  </Grid>
                )}

                {selectedLog.dados_depois && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Dados Depois da Alteração</Typography>
                    <Box sx={{ pl: 1 }}>
                      <pre style={{ fontSize: '0.875rem', overflow: 'auto', backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px' }}>
                        {formatJsonData(selectedLog.dados_depois)}
                      </pre>
                    </Box>
                  </Grid>
                )}

                {selectedLog.metadata && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Metadados</Typography>
                    <Box sx={{ pl: 1 }}>
                      <pre style={{ fontSize: '0.875rem', overflow: 'auto', backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px' }}>
                        {formatJsonData(selectedLog.metadata)}
                      </pre>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLogDetails(false)}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Configurações de Logs */}
        <LogSettings
          open={showLogSettings}
          onClose={() => setShowLogSettings(false)}
        />

        {/* Modal de Execuções do Cron */}
        <CronExecutionsModal
          open={showCronExecutions}
          onClose={() => setShowCronExecutions(false)}
        />
      </Box>
  );
};

export default LogsPage; 